document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

const getFocusableElements = (container) => Array.from(container.querySelectorAll(focusableSelector))
  .filter((element) => !element.hidden && element.tabIndex !== -1 && element.getClientRects().length > 0);

const normalizeQuantityValue = (value, minimum, maximum, increment) => {
  const highestValidValue = Number.isFinite(maximum)
    ? Math.max(minimum, minimum + (Math.floor((maximum - minimum) / increment) * increment))
    : Infinity;
  const steppedValue = minimum + (Math.max(0, Math.ceil((value - minimum) / increment)) * increment);
  return Math.min(highestValidValue, steppedValue);
};

let cartDrawerReturnFocus = null;

const getCartDrawer = () => document.querySelector('[data-cart-drawer]');

const updateBodyScrollLock = () => {
  const drawerOpen = getCartDrawer()?.classList.contains('is-open');
  const mediaModalOpen = document.querySelector('[data-media-modal][open]');
  const modalOpen = document.querySelector('[data-modal]:not([hidden])');
  const mobileNavigationOpen = document.querySelector('[data-mobile-navigation][open]');
  const facetsOpen = document.querySelector('[data-facets].is-open');
  const searchOpen = document.querySelector('.site-header.is-search-open');
  document.body.classList.toggle(
    'is-scroll-locked',
    Boolean(drawerOpen || mediaModalOpen || modalOpen || mobileNavigationOpen || facetsOpen || searchOpen)
  );
};

const updateCartCount = (drawer) => {
  const count = Number(drawer?.dataset.cartItemCount) || 0;
  const labelTemplate = drawer?.dataset.cartLabelTemplate || '';

  document.querySelectorAll('[data-cart-count]').forEach((element) => {
    const previous = element.textContent.trim();
    element.textContent = String(count);
    element.hidden = count === 0;
    if (previous !== String(count) && count > 0) {
      element.classList.remove('is-bumped');
      requestAnimationFrame(() => element.classList.add('is-bumped'));
    }
  });
  document.querySelectorAll('[data-cart-drawer-open]').forEach((trigger) => {
    if (labelTemplate) trigger.setAttribute('aria-label', labelTemplate.replace('__count__', String(count)));
  });
};

const openCartDrawer = (trigger) => {
  const drawer = getCartDrawer();
  if (!drawer) return;

  cartDrawerReturnFocus = trigger || document.activeElement;
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  document.querySelectorAll('[data-cart-drawer-open]').forEach((element) => element.setAttribute('aria-expanded', 'true'));
  updateBodyScrollLock();
  loadCartRecommendations(drawer);
  requestAnimationFrame(() => drawer.querySelector('[data-cart-drawer-close]:not([tabindex="-1"])')?.focus());
};

const closeCartDrawer = ({ restoreFocus = true } = {}) => {
  const drawer = getCartDrawer();
  if (!drawer?.classList.contains('is-open')) return;

  drawer.classList.remove('is-open');
  drawer.setAttribute('aria-hidden', 'true');
  document.querySelectorAll('[data-cart-drawer-open]').forEach((element) => element.setAttribute('aria-expanded', 'false'));
  updateBodyScrollLock();
  if (restoreFocus && cartDrawerReturnFocus instanceof HTMLElement) cartDrawerReturnFocus.focus();
};

const setCartDrawerBusy = (drawer, busy, message = '') => {
  drawer?.setAttribute('aria-busy', String(busy));
  const status = drawer?.querySelector('[data-cart-drawer-status]');
  if (status && message) status.textContent = message;
};

const showCartDrawerError = (drawer, message) => {
  const error = drawer?.querySelector('[data-cart-drawer-error]');
  if (!error) return;
  error.textContent = message;
  error.hidden = false;
  error.focus();
};

const getResponseError = (data, fallback) => data?.description || data?.message || fallback;

const renderCartDrawer = (drawer, sectionHtml) => {
  if (!drawer || !sectionHtml) return false;
  const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const nextDrawer = parsedDocument.querySelector('[data-cart-drawer]');
  if (!nextDrawer) return false;

  drawer.innerHTML = nextDrawer.innerHTML;
  drawer.dataset.cartItemCount = nextDrawer.dataset.cartItemCount;
  drawer.dataset.cartLabelTemplate = nextDrawer.dataset.cartLabelTemplate;
  drawer.dataset.sectionId = nextDrawer.dataset.sectionId;
  drawer.dataset.cartAddUrl = nextDrawer.dataset.cartAddUrl;
  drawer.dataset.cartChangeUrl = nextDrawer.dataset.cartChangeUrl;
  drawer.dataset.cartUrl = nextDrawer.dataset.cartUrl;
  drawer.dataset.addingLabel = nextDrawer.dataset.addingLabel;
  drawer.dataset.addedLabel = nextDrawer.dataset.addedLabel;
  drawer.dataset.addError = nextDrawer.dataset.addError;
  drawer.dataset.updatingLabel = nextDrawer.dataset.updatingLabel;
  drawer.dataset.updateError = nextDrawer.dataset.updateError;
  updateCartCount(drawer);
  if (drawer.classList.contains('is-open')) loadCartRecommendations(drawer);
  return true;
};

const fetchCartRecommendations = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const content = new DOMParser().parseFromString(await response.text(), 'text/html')
      .querySelector('[data-cart-recommendations-content]');
    return content?.querySelector('li') ? content : null;
  } catch (error) {
    return null;
  }
};

// Complementary picks come from Search & Discovery; related products are Shopify's automatic fallback.
const loadCartRecommendations = async (drawer) => {
  const container = drawer?.querySelector('[data-cart-recommendations]');
  if (!container || container.dataset.loaded === 'true' || !window.fetch) return;
  container.dataset.loaded = 'true';
  const content = await fetchCartRecommendations(`${container.dataset.url}&intent=complementary`)
    || await fetchCartRecommendations(`${container.dataset.url}&intent=related`);
  if (!content || !container.isConnected) return;
  container.querySelector('[data-cart-recommendations-list]').innerHTML = content.innerHTML;
  container.hidden = false;
};

const refreshCartDrawer = async (drawer) => {
  const response = await fetch(`${drawer.dataset.cartUrl}?section_id=${encodeURIComponent(drawer.dataset.sectionId)}`);
  if (!response.ok) throw new Error(drawer.dataset.updateError);
  return renderCartDrawer(drawer, await response.text());
};

const getCartSectionHtml = (data, drawer) => data?.sections?.[drawer.dataset.sectionId];

const updateCartLine = async (line, quantity) => {
  const drawer = getCartDrawer();
  if (!drawer || drawer.getAttribute('aria-busy') === 'true') return;

  setCartDrawerBusy(drawer, true, drawer.dataset.updatingLabel);
  const existingError = drawer.querySelector('[data-cart-drawer-error]');
  if (existingError) existingError.hidden = true;

  try {
    const response = await fetch(drawer.dataset.cartChangeUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        line,
        quantity,
        sections: [drawer.dataset.sectionId],
        sections_url: window.location.pathname
      })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(getResponseError(data, drawer.dataset.updateError));
    if (!renderCartDrawer(drawer, getCartSectionHtml(data, drawer))) await refreshCartDrawer(drawer);
    const lineInputs = Array.from(drawer.querySelectorAll('[data-cart-line-input]'));
    const focusTarget = lineInputs[Math.min(Math.max(line - 1, 0), lineInputs.length - 1)]
      || drawer.querySelector('[data-cart-drawer-close]:not([tabindex="-1"])');
    requestAnimationFrame(() => focusTarget?.focus());
  } catch (error) {
    showCartDrawerError(drawer, error.message || drawer.dataset.updateError);
  } finally {
    setCartDrawerBusy(drawer, false);
  }
};

const showProductFormError = (form, message) => {
  const error = form.querySelector('[data-product-form-error]');
  if (!error) return;
  error.textContent = message;
  error.hidden = false;
  error.focus();
};

const submitProductForm = async (form, submitter) => {
  const drawer = getCartDrawer();
  if (!drawer || form.getAttribute('aria-busy') === 'true') return;

  const button = submitter || form.querySelector('[type="submit"][name="add"]');
  const originalMarkup = button?.innerHTML || '';
  const usesTextLabel = Boolean(button?.matches('[data-add-to-cart], [data-sticky-submit]'));
  const error = form.querySelector('[data-product-form-error]');
  const status = form.querySelector('[data-product-form-status]');
  if (error) error.hidden = true;
  if (status) status.textContent = drawer.dataset.addingLabel;
  form.setAttribute('aria-busy', 'true');
  if (button) {
    button.disabled = true;
    button.classList.add('is-loading');
  }

  try {
    const formData = new FormData(form);
    formData.set('sections', drawer.dataset.sectionId);
    formData.set('sections_url', window.location.pathname);
    const response = await fetch(drawer.dataset.cartAddUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(getResponseError(data, drawer.dataset.addError));
    if (!renderCartDrawer(drawer, getCartSectionHtml(data, drawer))) await refreshCartDrawer(drawer);
    button?.classList.remove('is-loading');
    if (button && usesTextLabel) button.textContent = drawer.dataset.addedLabel;
    if (status) status.textContent = drawer.dataset.addedLabel;
    const containingModal = form.closest('[data-modal]');
    if (containingModal) closeModal(containingModal, { restoreFocus: false });
    openCartDrawer(button?.isConnected ? button : document.activeElement);
  } catch (requestError) {
    const message = requestError.message || drawer.dataset.addError;
    button?.classList.remove('is-loading');
    showProductFormError(form, message);
    if (status) status.textContent = message;
  } finally {
    form.removeAttribute('aria-busy');
    if (button) {
      button.disabled = false;
      window.setTimeout(() => {
        if (button.isConnected && form.getAttribute('aria-busy') !== 'true') button.innerHTML = originalMarkup;
      }, 1200);
    }
  }
};

document.addEventListener('submit', (event) => {
  const form = event.target.closest('.product-form, .product-card__form');
  if (!form || !getCartDrawer() || !window.fetch || !window.FormData) return;
  if (!event.submitter?.matches('[name="add"], [data-add-to-cart], [data-sticky-submit]')) return;
  event.preventDefault();
  submitProductForm(form, event.submitter);
});

document.addEventListener('click', (event) => {
  const openTrigger = event.target.closest('[data-cart-drawer-open]');
  if (openTrigger) {
    event.preventDefault();
    openCartDrawer(openTrigger);
    return;
  }

  const closeTrigger = event.target.closest('[data-cart-drawer-close]');
  if (closeTrigger) {
    closeCartDrawer();
    return;
  }

  const quantityButton = event.target.closest('[data-cart-line-change]');
  if (quantityButton) {
    const input = quantityButton.closest('.cart-drawer__quantity')?.querySelector('[data-cart-line-input]');
    if (!input) return;
    const currentQuantity = Number(input.value) || 0;
    const minimum = Number(input.min) || 1;
    const maximum = input.max ? Number(input.max) : Infinity;
    const step = Number(input.step) || 1;
    const nextQuantity = currentQuantity + (Number(quantityButton.dataset.cartLineChange) * step);
    if (nextQuantity < minimum || nextQuantity > maximum) return;
    updateCartLine(Number(quantityButton.dataset.line), nextQuantity);
    return;
  }

  const removeTrigger = event.target.closest('[data-cart-line-remove]');
  if (removeTrigger) {
    event.preventDefault();
    updateCartLine(Number(removeTrigger.dataset.line), 0);
  }
});

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-cart-line-input]');
  if (!input) return;
  const minimum = Number(input.min) || 1;
  const maximum = input.max ? Number(input.max) : Infinity;
  const increment = Number(input.step) || 1;
  const quantity = normalizeQuantityValue(Number(input.value) || minimum, minimum, maximum, increment);
  input.value = String(quantity);
  updateCartLine(Number(input.dataset.line), quantity);
});

document.addEventListener('keydown', (event) => {
  const drawer = getCartDrawer();
  if (!drawer?.classList.contains('is-open')) return;

  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopImmediatePropagation();
    closeCartDrawer();
    return;
  }

  if (event.key !== 'Tab') return;
  const focusableElements = getFocusableElements(drawer);
  if (!focusableElements.length) return;
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  }
});

updateCartCount(getCartDrawer());

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  const openDetails = Array.from(document.querySelectorAll('details[open]'));
  const activeDetails = document.activeElement?.closest('details[open]')
    || openDetails.find((details) => details.contains(document.activeElement));

  if (!activeDetails) return;
  activeDetails.removeAttribute('open');
  activeDetails.querySelector(':scope > summary')?.focus();
});

document.addEventListener('click', (event) => {
  document.querySelectorAll('.site-nav details[open]').forEach((details) => {
    if (!details.contains(event.target)) details.removeAttribute('open');
  });
});

const queryWithin = (root, selector) => {
  const matchesRoot = root instanceof Element && root.matches(selector) ? [root] : [];
  return [...matchesRoot, ...root.querySelectorAll(selector)];
};

const initializeMobileNavigation = (root) => {
  queryWithin(root, '[data-mobile-navigation]').forEach((navigation) => {
    if (navigation.dataset.navigationInitialized === 'true') return;
    navigation.dataset.navigationInitialized = 'true';
    const toggle = navigation.querySelector(':scope > summary');
    const panel = navigation.querySelector('.menu-drawer__panel');
    const closeButtons = navigation.querySelectorAll('[data-mobile-navigation-close]');

    const close = ({ restoreFocus = true } = {}) => {
      if (!navigation.open) return;
      navigation.open = false;
      updateBodyScrollLock();
      if (restoreFocus) requestAnimationFrame(() => toggle?.focus());
    };

    navigation.addEventListener('toggle', () => {
      toggle?.setAttribute(
        'aria-label',
        navigation.open ? toggle.dataset.closeLabel : toggle.dataset.openLabel
      );
      toggle?.setAttribute('aria-expanded', String(navigation.open));
      updateBodyScrollLock();
      if (navigation.open) {
        document.querySelectorAll('[data-mobile-navigation][open]').forEach((otherNavigation) => {
          if (otherNavigation !== navigation) otherNavigation.open = false;
        });
        requestAnimationFrame(() => getFocusableElements(panel || navigation)[0]?.focus());
      }
    });
    closeButtons.forEach((button) => button.addEventListener('click', () => close()));
    navigation.addEventListener('keydown', (event) => {
      if (!navigation.open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusableElements = getFocusableElements(navigation);
      if (!focusableElements.length) return;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    });
  });
};

const productSectionCleanups = new WeakMap();

/* Recently viewed products: handles stay in this browser only. */

const RECENTLY_VIEWED_KEY = 'moogoo:recently-viewed';

const readRecentlyViewed = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(RECENTLY_VIEWED_KEY));
    return Array.isArray(stored) ? stored.filter((handle) => typeof handle === 'string') : [];
  } catch (error) {
    return [];
  }
};

const writeRecentlyViewed = (handles) => {
  try {
    window.localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(handles.slice(0, 12)));
  } catch (error) {
    // Storage can be unavailable in private browsing.
  }
};

const rememberRecentlyViewed = (handle) => {
  if (!handle) return;
  writeRecentlyViewed([handle, ...readRecentlyViewed().filter((stored) => stored !== handle)]);
};

class RecentlyViewed extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.querySelector('[data-recently-viewed-clear]')?.addEventListener('click', () => {
      writeRecentlyViewed([]);
      this.hidden = true;
    });
    this.render();
  }

  async render() {
    const handles = readRecentlyViewed()
      .filter((handle) => handle !== this.dataset.productHandle)
      .slice(0, Number(this.dataset.limit) || 4);
    if (!handles.length || !window.fetch) return;
    const root = window.Shopify?.routes?.root || '/';
    const missing = [];
    const cards = await Promise.all(handles.map(async (handle) => {
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=product-card-fragment`);
        if (response.status === 404) missing.push(handle);
        if (!response.ok) return null;
        return new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-product-card-fragment]');
      } catch (error) {
        return null;
      }
    }));
    if (missing.length) writeRecentlyViewed(readRecentlyViewed().filter((handle) => !missing.includes(handle)));
    const list = this.querySelector('[data-recently-viewed-list]');
    cards.filter(Boolean).forEach((card) => list.append(card));
    this.hidden = list.children.length === 0;
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewed);
}

/* Wishlist: product handles stay in this browser; the wishlist page can share them by link. */

const WISHLIST_KEY = 'moogoo:wishlist';
const WISHLIST_LIMIT = 60;
const WISHLIST_HANDLE_PATTERN = /^[\p{L}\p{N}][\p{L}\p{N}_-]{0,254}$/u;

const sanitizeWishlist = (handles) => Array.from(new Set(
  handles.filter((handle) => typeof handle === 'string' && WISHLIST_HANDLE_PATTERN.test(handle))
)).slice(0, WISHLIST_LIMIT);

const readWishlist = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(WISHLIST_KEY));
    return Array.isArray(stored) ? sanitizeWishlist(stored) : [];
  } catch (error) {
    return [];
  }
};

const syncWishlistButtons = (root = document, handles = readWishlist()) => {
  queryWithin(root, '[data-wishlist-toggle]').forEach((button) => {
    const saved = handles.includes(button.dataset.wishlistToggle);
    button.setAttribute('aria-pressed', String(saved));
    const label = saved ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label) button.setAttribute('aria-label', label);
  });
};

const updateWishlistCount = (handles = readWishlist()) => {
  document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
    count.textContent = String(handles.length);
    count.hidden = handles.length === 0;
  });
};

const writeWishlist = (handles) => {
  const next = sanitizeWishlist(handles);
  try {
    window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(next));
  } catch (error) {
    // Storage can be unavailable in private browsing.
  }
  syncWishlistButtons(document, next);
  updateWishlistCount(next);
  document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles: next } }));
  return next;
};

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-wishlist-toggle]');
  if (!button) return;
  event.preventDefault();
  const handle = button.dataset.wishlistToggle;
  const handles = readWishlist();
  const saved = handles.includes(handle);
  writeWishlist(saved ? handles.filter((stored) => stored !== handle) : [handle, ...handles]);
  const status = document.querySelector('[data-global-status]');
  announce(saved ? status?.dataset.wishlistRemoved : status?.dataset.wishlistAdded);
  button.classList.remove('is-bumped');
  if (!saved) {
    void button.offsetWidth;
    button.classList.add('is-bumped');
  }
});

window.addEventListener('storage', (event) => {
  if (event.key !== WISHLIST_KEY) return;
  const handles = readWishlist();
  syncWishlistButtons(document, handles);
  updateWishlistCount(handles);
  document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles } }));
});

// Cards also arrive through filtering, recommendations, quick view and search; keep their hearts in sync.
if ('MutationObserver' in window) {
  new MutationObserver((mutations) => {
    let handles;
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (!node.matches('[data-wishlist-toggle]') && !node.querySelector('[data-wishlist-toggle]')) return;
        handles = handles || readWishlist();
        syncWishlistButtons(node, handles);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
}

class WishlistPage extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.cards = new Map();
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.summary = this.querySelector('[data-wishlist-summary]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.loading = this.querySelector('[data-wishlist-loading]');
    this.sharedNotice = this.querySelector('[data-wishlist-shared]');
    this.shareButton = this.querySelector('[data-wishlist-share]');
    this.clearButton = this.querySelector('[data-wishlist-clear]');

    const sharedParam = new URLSearchParams(window.location.search).get('items');
    this.sharedHandles = sharedParam ? sanitizeWishlist(sharedParam.split(',')) : null;
    if (this.sharedHandles && !this.sharedHandles.length) this.sharedHandles = null;

    this.clearButton?.addEventListener('click', () => writeWishlist([]));
    this.querySelector('[data-wishlist-save-shared]')?.addEventListener('click', () => {
      const shared = this.sharedHandles || [];
      this.sharedHandles = null;
      window.history.replaceState({}, '', window.location.pathname);
      writeWishlist([...shared, ...readWishlist()]);
    });
    this.onWishlistChange = () => {
      if (!this.sharedHandles) this.render();
    };
    document.addEventListener('wishlist:change', this.onWishlistChange);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:change', this.onWishlistChange);
  }

  async fetchCard(handle) {
    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=product-card-fragment`);
      if (response.status === 404) return 'missing';
      if (!response.ok) return null;
      const card = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-product-card-fragment]');
      if (card) this.cards.set(handle, card);
      return card;
    } catch (error) {
      return null;
    }
  }

  async render() {
    const renderId = (this.renderId || 0) + 1;
    this.renderId = renderId;
    const shared = Boolean(this.sharedHandles);
    const handles = this.sharedHandles || readWishlist();
    this.sharedNotice.hidden = !shared;
    this.clearButton.hidden = shared || handles.length === 0;

    const missing = handles.filter((handle) => !this.cards.has(handle));
    this.loading.hidden = missing.length === 0;
    const results = await Promise.all(missing.map((handle) => this.fetchCard(handle)));
    if (renderId !== this.renderId) return;
    this.loading.hidden = true;

    const cards = handles.map((handle) => this.cards.get(handle)).filter(Boolean);
    this.grid.replaceChildren(...cards);
    this.empty.hidden = cards.length > 0;
    this.grid.hidden = cards.length === 0;
    this.shareButton.hidden = cards.length === 0;
    if (cards.length) {
      const shareUrl = new URL(window.location.pathname, window.location.origin);
      shareUrl.searchParams.set('items', handles.filter((handle) => this.cards.has(handle)).join(','));
      this.shareButton.dataset.shareUrl = shareUrl.toString();
    }
    const template = cards.length === 1 ? this.summary.dataset.countOne : this.summary.dataset.countOther;
    this.summary.textContent = cards.length ? (template || '').replace('__count__', String(cards.length)) : '';

    const gone = missing.filter((handle, index) => results[index] === 'missing');
    if (gone.length && !shared) writeWishlist(readWishlist().filter((handle) => !gone.includes(handle)));
  }
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}

/* Product reviews: sort, star filter and paging over server-rendered review cards. */

class ProductReviews extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.list = this.querySelector('[data-review-list]');
    this.items = Array.from(this.querySelectorAll('[data-review]'));
    this.items.forEach((item, index) => { item.dataset.index = String(index); });
    this.pageSize = Number(this.dataset.pageSize) || 5;
    this.visible = this.pageSize;
    this.filter = 0;
    this.sort = 'featured';
    this.results = this.querySelector('[data-review-results]');
    this.moreButton = this.querySelector('[data-review-more]');
    this.clearButton = this.querySelector('[data-review-filter-clear]');
    this.filterButtons = Array.from(this.querySelectorAll('[data-review-filter]'));

    this.querySelector('[data-review-sort]')?.addEventListener('change', (event) => {
      this.sort = event.target.value;
      this.visible = this.pageSize;
      this.update();
    });
    this.filterButtons.forEach((button) => button.addEventListener('click', () => {
      const rating = Number(button.dataset.reviewFilter);
      this.filter = this.filter === rating ? 0 : rating;
      this.visible = this.pageSize;
      this.update();
    }));
    this.clearButton?.addEventListener('click', () => {
      this.filter = 0;
      this.visible = this.pageSize;
      this.update();
    });
    this.moreButton?.addEventListener('click', () => {
      const firstHidden = this.visible;
      this.visible += this.pageSize;
      this.update();
      this.matching[firstHidden]?.focus();
    });
    this.initializeForm();
    this.update();
  }

  initializeForm() {
    const panel = this.querySelector('[data-review-form]');
    const toggles = Array.from(this.querySelectorAll('[data-review-form-toggle]'));
    if (!panel) return;
    const status = panel.querySelector('[data-review-form-status]');
    const setOpen = (open) => {
      panel.hidden = !open;
      toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', String(open)));
    };
    setOpen(Boolean(status));
    if (status) {
      panel.scrollIntoView({ block: 'start' });
      status.focus({ preventScroll: true });
    }
    toggles.forEach((toggle) => toggle.addEventListener('click', () => {
      const open = panel.hidden;
      setOpen(open);
      if (!open) return;
      panel.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      panel.querySelector('input[type="radio"]')?.focus({ preventScroll: true });
    }));
  }

  update() {
    if (!this.list) return;
    const byIndex = (a, b) => Number(a.dataset.index) - Number(b.dataset.index);
    const comparators = {
      featured: byIndex,
      newest: (a, b) => (b.dataset.date || '').localeCompare(a.dataset.date || '') || byIndex(a, b),
      highest: (a, b) => Number(b.dataset.rating) - Number(a.dataset.rating) || byIndex(a, b),
      lowest: (a, b) => Number(a.dataset.rating) - Number(b.dataset.rating) || byIndex(a, b),
      photos: (a, b) => Number(b.dataset.photo === 'true') - Number(a.dataset.photo === 'true') || byIndex(a, b)
    };
    const sorted = [...this.items].sort(comparators[this.sort] || byIndex);
    this.list.append(...sorted);
    this.matching = sorted.filter((item) => !this.filter || Number(item.dataset.rating) === this.filter);
    sorted.forEach((item) => { item.hidden = true; });
    this.matching.slice(0, this.visible).forEach((item) => { item.hidden = false; });

    if (this.moreButton) this.moreButton.hidden = this.matching.length <= this.visible;
    if (this.clearButton) this.clearButton.hidden = !this.filter;
    this.filterButtons.forEach((button) => {
      button.setAttribute('aria-pressed', String(Number(button.dataset.reviewFilter) === this.filter));
    });
    if (this.results) {
      this.results.textContent = (this.dataset.resultsTemplate || '')
        .replace('__shown__', String(Math.min(this.visible, this.matching.length)))
        .replace('__total__', String(this.matching.length));
    }
  }
}

if (!customElements.get('product-reviews')) {
  customElements.define('product-reviews', ProductReviews);
}

/* Product media tracks: native scroll-snap swiping with previous/next buttons. */

const loadNearbyMediaImages = (items, index) => {
  [index - 1, index, index + 1].forEach((nearbyIndex) => {
    const image = items[nearbyIndex]?.querySelector('img[loading="lazy"]');
    if (image) image.loading = 'eager';
  });
};

const initializeMediaTrack = (track, items, { previous, next, onChange }) => {
  let activeIndex = -1;
  let targetIndex = null;
  let settleTimer = 0;
  let trackWidth = 0;
  const clampIndex = (index) => Math.min(items.length - 1, Math.max(0, index));

  const setActive = (index, instant = false) => {
    if (index === activeIndex) return;
    activeIndex = index;
    previous?.setAttribute('aria-disabled', String(index === 0));
    next?.setAttribute('aria-disabled', String(index === items.length - 1));
    onChange(index, { instant });
  };

  const syncWithScroll = () => {
    if (!track.clientWidth) return;
    const index = clampIndex(Math.round(track.scrollLeft / track.clientWidth));
    // A button or thumbnail scroll keeps its target selected while passing other slides.
    if (targetIndex === null || index === targetIndex) setActive(index);
  };

  const onScroll = () => {
    syncWithScroll();
    window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(() => {
      targetIndex = null;
      syncWithScroll();
    }, 150);
  };

  const goTo = (index, { instant = false } = {}) => {
    const nextIndex = clampIndex(index);
    targetIndex = nextIndex;
    setActive(nextIndex, instant);
    track.scrollTo({ left: items[nextIndex].offsetLeft, behavior: instant || prefersReducedMotion() ? 'auto' : 'smooth' });
    onScroll();
  };

  const releaseTarget = () => {
    targetIndex = null;
  };

  track.addEventListener('scroll', onScroll, { passive: true });
  track.addEventListener('pointerdown', releaseTarget, { passive: true });
  track.addEventListener('wheel', releaseTarget, { passive: true });
  previous?.addEventListener('click', () => goTo(activeIndex - 1));
  next?.addEventListener('click', () => goTo(activeIndex + 1));

  const resizeObserver = 'ResizeObserver' in window
    ? new ResizeObserver(() => {
      if (!track.clientWidth || track.clientWidth === trackWidth || activeIndex < 0) return;
      trackWidth = track.clientWidth;
      track.scrollTo({ left: items[activeIndex].offsetLeft, behavior: 'auto' });
    })
    : null;
  resizeObserver?.observe(track);

  return {
    goTo,
    getIndex: () => activeIndex,
    disconnect: () => resizeObserver?.disconnect()
  };
};

const initializeProductSection = (section) => {
  if (section.dataset.productInitialized === 'true') return;

  const variantsElement = section.querySelector('[data-product-variants]');
  const optionGroups = Array.from(section.querySelectorAll('[data-option-group]'));
  const optionInputs = Array.from(section.querySelectorAll('[data-option-input]'));
  const variantInput = section.querySelector('[data-variant-id]');
  const submitButton = section.querySelector('[data-add-to-cart]');
  const currentPrice = section.querySelector('[data-current-price]');
  const comparePrice = section.querySelector('[data-compare-price]');
  const salePriceLabel = section.querySelector('[data-sale-price-label]');
  const regularPriceLabel = section.querySelector('[data-regular-price-label]');
  const priceContainer = section.querySelector('[data-product-price]');
  const saleBadge = section.querySelector('[data-sale-badge]');
  const dynamicCheckout = section.querySelector('[data-dynamic-checkout]');
  const stickyCart = section.querySelector('[data-sticky-cart]');
  const stickySubmit = section.querySelector('[data-sticky-submit]');
  const stickyPrice = section.querySelector('[data-sticky-price]');
  const stickyVariant = section.querySelector('[data-sticky-variant]');
  const unitPrice = section.querySelector('[data-unit-price]');
  const inventoryStatus = section.querySelector('[data-inventory-status]');
  const mediaGallery = section.querySelector('[data-media-gallery]');
  const mediaTrackElement = section.querySelector('[data-media-track]');
  const mediaItems = Array.from(section.querySelectorAll('[data-media-id]'));
  const mediaThumbnailList = section.querySelector('.product-media__thumbnails');
  const mediaThumbnails = Array.from(section.querySelectorAll('[data-media-thumbnail]'));
  const mediaCurrent = section.querySelector('[data-media-current]');
  const mediaModal = section.querySelector('[data-media-modal]');
  const mediaModalTrackElement = section.querySelector('[data-media-modal-track]');
  const mediaModalItems = Array.from(section.querySelectorAll('[data-media-modal-item]'));
  const mediaModalCurrent = section.querySelector('[data-media-modal-current]');

  if (!variantsElement || !variantInput || !submitButton) return;

  let variants;
  try {
    variants = JSON.parse(variantsElement.textContent);
  } catch (error) {
    console.error('Unable to initialize product variants.', error);
    return;
  }

  section.dataset.productInitialized = 'true';
  const cleanupCallbacks = [];
  if (!section.closest('[data-modal]')) rememberRecentlyViewed(section.dataset.productHandle);

  const updateInventoryStatus = (variant) => {
    if (!inventoryStatus) return;
    const threshold = Number(inventoryStatus.dataset.lowThreshold) || 5;
    const quantity = Number(variant?.inventoryQuantity);
    let state = '';
    if (variant?.available && variant.inventoryTracked) {
      if (quantity <= 0) state = 'backorder';
      else if (variant.inventoryPolicy === 'deny' && quantity <= threshold) state = 'low';
      else state = 'in';
    }
    const text = {
      in: inventoryStatus.dataset.inLabel,
      low: (inventoryStatus.dataset.lowTemplate || '').replace('__count__', String(quantity)),
      backorder: inventoryStatus.dataset.backorderLabel
    }[state];
    inventoryStatus.hidden = !text;
    inventoryStatus.classList.remove('inventory-status--in', 'inventory-status--low', 'inventory-status--backorder');
    if (!text) return;
    inventoryStatus.classList.add(`inventory-status--${state}`);
    inventoryStatus.querySelector('[data-inventory-status-text]').textContent = text;
  };

  const recipientToggle = section.querySelector('[data-recipient-toggle]');
  if (recipientToggle) {
    const recipientFields = section.querySelector('[data-recipient-fields]');
    const offsetInput = section.querySelector('[data-recipient-offset]');
    const sendOnInput = section.querySelector('[data-recipient-send-on]');
    if (offsetInput) offsetInput.value = String(new Date().getTimezoneOffset());
    if (sendOnInput) {
      const today = new Date();
      const maximum = new Date();
      maximum.setDate(today.getDate() + 90);
      const toIsoDate = (date) => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
      sendOnInput.min = toIsoDate(today);
      sendOnInput.max = toIsoDate(maximum);
    }
    const syncRecipient = () => {
      recipientFields.disabled = !recipientToggle.checked;
      recipientFields.hidden = !recipientToggle.checked;
    };
    recipientToggle.addEventListener('change', syncRecipient);
    syncRecipient();
  }

  let activeMediaIndex = 0;
  let mediaTrack = null;
  const revealThumbnail = (thumbnail, instant) => {
    if (!mediaThumbnailList) return;
    const listBounds = mediaThumbnailList.getBoundingClientRect();
    const bounds = thumbnail.getBoundingClientRect();
    mediaThumbnailList.scrollBy({
      left: Math.min(0, bounds.left - listBounds.left) + Math.max(0, bounds.right - listBounds.right),
      top: Math.min(0, bounds.top - listBounds.top) + Math.max(0, bounds.bottom - listBounds.bottom),
      behavior: instant || prefersReducedMotion() ? 'auto' : 'smooth'
    });
  };
  const activateMedia = (mediaId, { instant = false } = {}) => {
    const index = mediaItems.findIndex((item) => item.dataset.mediaId === String(mediaId));
    if (index >= 0) mediaTrack?.goTo(index, { instant });
  };

  if (mediaTrackElement && mediaItems.length) {
    // Show every slide only once the track can be positioned, so the first paint keeps the active media.
    mediaTrackElement.classList.add('is-ready');
    mediaTrack = initializeMediaTrack(mediaTrackElement, mediaItems, {
      previous: section.querySelector('[data-media-previous]'),
      next: section.querySelector('[data-media-next]'),
      onChange: (index, { instant }) => {
        const activeItem = mediaItems[index];
        activeMediaIndex = index;
        mediaItems.forEach((item) => {
          const isActive = item === activeItem;
          item.classList.toggle('is-active', isActive);
          item.inert = !isActive;
          if (!isActive) item.querySelectorAll('video').forEach((video) => video.pause());
        });
        mediaThumbnails.forEach((thumbnail) => {
          const isActive = thumbnail.dataset.mediaThumbnail === activeItem.dataset.mediaId;
          thumbnail.setAttribute('aria-current', String(isActive));
          if (isActive) revealThumbnail(thumbnail, instant);
        });
        if (mediaCurrent) mediaCurrent.textContent = activeItem.dataset.mediaPosition;
        if (mediaGallery) mediaGallery.dataset.activeMediaType = activeItem.dataset.mediaType || '';
        // Page load keeps the other media lazy; neighbors load once the shopper starts browsing.
        if (!instant) loadNearbyMediaImages(mediaItems, index);
      }
    });
    cleanupCallbacks.push(mediaTrack.disconnect);
    mediaThumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => activateMedia(thumbnail.dataset.mediaThumbnail));
    });
    activateMedia(mediaGallery?.dataset.activeMediaId || mediaItems[0].dataset.mediaId, { instant: true });
  }

  if (mediaModal && mediaModalTrackElement && mediaModalItems.length) {
    let mediaModalPointerType = '';
    const mediaModalTrack = initializeMediaTrack(mediaModalTrackElement, mediaModalItems, {
      previous: mediaModal.querySelector('[data-media-modal-previous]'),
      next: mediaModal.querySelector('[data-media-modal-next]'),
      onChange: (index) => {
        if (mediaModalCurrent) mediaModalCurrent.textContent = String(index + 1);
        loadNearbyMediaImages(mediaModalItems, index);
      }
    });

    section.querySelectorAll('[data-media-zoom]').forEach((button) => {
      button.addEventListener('click', () => {
        const index = mediaModalItems.findIndex((item) => item.dataset.mediaModalItem === button.dataset.mediaZoom);
        if (index < 0) return;
        mediaModal.showModal();
        updateBodyScrollLock();
        mediaModalTrack.goTo(index, { instant: true });
      });
    });

    // Covers the close button, outside clicks, Escape and platform back gestures.
    mediaModal.addEventListener('close', () => {
      updateBodyScrollLock();
      activateMedia(mediaModalItems[mediaModalTrack.getIndex()].dataset.mediaModalItem, { instant: true });
      mediaItems[activeMediaIndex]?.querySelector('[data-media-zoom]')?.focus({ preventScroll: true });
    });
    mediaModal.addEventListener('pointerdown', (event) => {
      mediaModalPointerType = event.pointerType;
    });
    mediaModal.addEventListener('click', (event) => {
      const { target } = event;
      // Touch taps on the photo itself keep it open, so browsing never dismisses it by accident.
      const closes = target.matches('[data-media-modal-item]')
        || target.closest('[data-media-modal-close]')
        || (target.matches('img') && mediaModalPointerType === 'mouse');
      if (closes) mediaModal.close();
    });
    mediaModal.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      mediaModalTrack.goTo(mediaModalTrack.getIndex() + (event.key === 'ArrowRight' ? 1 : -1));
    });
    cleanupCallbacks.push(() => {
      mediaModalTrack.disconnect();
      if (mediaModal.open) mediaModal.close();
    });
  }

  const getSelectedOptions = () => optionGroups.map(
    (group) => group.querySelector('[data-option-input]:checked')?.value || ''
  );

  const updateOptionAvailability = (selectedOptions) => {
    optionGroups.forEach((group, optionIndex) => {
      group.querySelectorAll('[data-option-input]').forEach((input) => {
        const candidates = variants.filter((candidate) =>
          candidate.options[optionIndex] === input.value
          && candidate.options.slice(0, optionIndex).every((option, index) =>
            !selectedOptions[index] || option === selectedOptions[index]
          )
        );
        const combinationExists = candidates.length > 0;
        const combinationAvailable = candidates.some((candidate) => candidate.available);
        const label = input.nextElementSibling;
        const status = label?.querySelector('[data-option-status]');

        input.disabled = !combinationExists;
        label?.classList.toggle('is-unavailable', !combinationAvailable);
        if (status) {
          status.hidden = combinationAvailable;
          status.textContent = combinationExists
            ? section.dataset.soldLabel
            : section.dataset.unavailableLabel;
        }
      });
    });
  };

  const updateVariant = ({ updateUrl = true } = {}) => {
    const selectedOptions = getSelectedOptions();
    const variant = optionGroups.length === 0
      ? variants[0]
      : variants.find((candidate) =>
        candidate.options.every((option, index) => option === selectedOptions[index])
      );

    optionGroups.forEach((group, index) => {
      const selectedValue = group.querySelector('[data-option-selected]');
      if (selectedValue) selectedValue.textContent = selectedOptions[index];
    });
    updateOptionAvailability(selectedOptions);

    if (stickyVariant) {
      const selectedOptionValues = selectedOptions.filter(Boolean);
      stickyVariant.textContent = selectedOptionValues.join(' / ');
      stickyVariant.hidden = selectedOptionValues.length === 0;
    }

    if (!variant) {
      variantInput.value = '';
      submitButton.disabled = false;
      submitButton.dataset.needsSelection = 'true';
      submitButton.textContent = section.dataset.chooseOptionsLabel;
      if (dynamicCheckout) dynamicCheckout.hidden = true;
      if (unitPrice) unitPrice.hidden = true;
      updateInventoryStatus(null);
      if (stickySubmit) {
        stickySubmit.disabled = false;
        stickySubmit.dataset.needsSelection = 'true';
        stickySubmit.textContent = section.dataset.chooseOptionsLabel;
      }
      return;
    }

    variantInput.value = variant.id;
    delete submitButton.dataset.needsSelection;
    submitButton.disabled = !variant.available;
    submitButton.textContent = variant.available
      ? submitButton.dataset.addLabel
      : submitButton.dataset.soldLabel;

    if (currentPrice) currentPrice.textContent = variant.price;
    if (comparePrice) {
      comparePrice.textContent = variant.compareAtPrice || '';
      comparePrice.hidden = !variant.compareAtPrice;
    }
    if (salePriceLabel) salePriceLabel.hidden = !variant.compareAtPrice;
    if (regularPriceLabel) regularPriceLabel.hidden = !variant.compareAtPrice;
    priceContainer?.classList.toggle('product-price--sale', Boolean(variant.compareAtPrice));
    if (saleBadge) {
      const onSale = Boolean(variant.compareAtPrice) && variant.compareAtCents > variant.priceCents;
      saleBadge.hidden = !onSale;
      if (onSale) {
        const percent = Math.round(((variant.compareAtCents - variant.priceCents) * 100) / variant.compareAtCents);
        saleBadge.textContent = (section.dataset.percentTemplate || '-__percent__%').replace('__percent__', String(percent));
      }
    }
    if (dynamicCheckout) dynamicCheckout.hidden = !variant.available;
    if (unitPrice) {
      unitPrice.hidden = !variant.unitPrice;
      const unitPriceValue = unitPrice.querySelector('[data-unit-price-value]');
      if (unitPriceValue) unitPriceValue.textContent = variant.unitPrice || '';
    }
    updateInventoryStatus(variant);
    if (stickyPrice) stickyPrice.textContent = variant.price;
    if (stickySubmit) {
      delete stickySubmit.dataset.needsSelection;
      stickySubmit.disabled = !variant.available;
      stickySubmit.textContent = variant.available
        ? submitButton.dataset.addLabel
        : submitButton.dataset.soldLabel;
    }

    if (quantityInput) {
      const minimum = Number(variant.quantityRule?.min) || 1;
      const maximum = variant.quantityRule?.max == null ? Infinity : Number(variant.quantityRule.max);
      const increment = Number(variant.quantityRule?.increment) || 1;
      const currentQuantity = Number(quantityInput.value) || minimum;
      const normalizedQuantity = normalizeQuantityValue(currentQuantity, minimum, maximum, increment);
      quantityInput.min = String(minimum);
      quantityInput.step = String(increment);
      if (Number.isFinite(maximum)) {
        quantityInput.max = String(maximum);
      } else {
        quantityInput.removeAttribute('max');
      }
      quantityInput.value = String(normalizedQuantity);
      updateQuantityButtons();
    }

    if (variant.featuredMediaId) activateMedia(variant.featuredMediaId);

    if (updateUrl && section.dataset.updateUrl === 'true') {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  };

  optionInputs.forEach((input) => input.addEventListener('change', () => {
    const group = input.closest('[data-option-group]');
    group?.classList.remove('is-invalid');
    const optionError = group?.querySelector('[data-option-error]');
    if (optionError) optionError.hidden = true;
    updateVariant();
  }));

  const promptOptionSelection = () => {
    const missingGroups = optionGroups.filter((group) => !group.querySelector('[data-option-input]:checked'));
    const target = missingGroups[0] || optionGroups[0];
    if (!target) return;
    missingGroups.forEach((group) => {
      group.classList.add('is-invalid');
      const optionError = group.querySelector('[data-option-error]');
      if (optionError) optionError.hidden = false;
    });
    target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    target.querySelector('[data-option-input]:not([disabled])')?.focus({ preventScroll: true });
    announce(missingGroups.length
      ? target.querySelector('[data-option-error]')?.textContent.trim()
      : section.dataset.unavailableLabel);
  };

  const onSubmitNeedsSelection = (event) => {
    if (event.currentTarget.dataset.needsSelection !== 'true') return;
    event.preventDefault();
    promptOptionSelection();
  };
  submitButton.addEventListener('click', onSubmitNeedsSelection);
  cleanupCallbacks.push(() => submitButton.removeEventListener('click', onSubmitNeedsSelection));

  const quantityInput = section.querySelector('[data-quantity-input]');
  const quantityButtons = Array.from(section.querySelectorAll('[data-quantity-change]'));
  const updateQuantityButtons = () => {
    if (!quantityInput) return;
    const value = Number(quantityInput.value);
    const minimum = Number(quantityInput.min) || 1;
    const maximum = quantityInput.max ? Number(quantityInput.max) : Infinity;
    const step = Number(quantityInput.step) || 1;
    quantityButtons.forEach((button) => {
      const direction = Number(button.dataset.quantityChange);
      button.disabled = direction < 0 ? value - step < minimum : value + step > maximum;
    });
  };

  const normalizeQuantity = () => {
    if (!quantityInput) return;
    const minimum = Number(quantityInput.min) || 1;
    const maximum = quantityInput.max ? Number(quantityInput.max) : Infinity;
    const step = Number(quantityInput.step) || 1;
    const currentValue = Number(quantityInput.value) || minimum;
    const normalizedValue = normalizeQuantityValue(currentValue, minimum, maximum, step);
    quantityInput.value = String(normalizedValue);
    updateQuantityButtons();
  };

  quantityButtons.forEach((button) => {
    button.addEventListener('click', () => {
      if (!quantityInput) return;
      const minimum = Number(quantityInput.min) || 1;
      const maximum = quantityInput.max ? Number(quantityInput.max) : Infinity;
      const step = Number(quantityInput.step) || 1;
      const currentValue = Number(quantityInput.value) || minimum;
      const nextValue = currentValue + (Number(button.dataset.quantityChange) * step);
      if (nextValue < minimum || nextValue > maximum) return;
      quantityInput.value = String(nextValue);
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
  quantityInput?.addEventListener('input', updateQuantityButtons);
  quantityInput?.addEventListener('change', normalizeQuantity);
  updateQuantityButtons();

  const purchaseArea = section.querySelector('[data-product-purchase]');
  if (stickyCart && purchaseArea && 'IntersectionObserver' in window) {
    let purchaseHasPassed = false;
    let footerIsVisible = false;
    const updateStickyCart = () => {
      const isVisible = purchaseHasPassed && !footerIsVisible;
      stickyCart.classList.toggle('is-visible', isVisible);
      stickyCart.setAttribute('aria-hidden', String(!isVisible));
    };

    const purchaseObserver = new IntersectionObserver(([entry]) => {
      purchaseHasPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      updateStickyCart();
    });
    purchaseObserver.observe(purchaseArea);
    cleanupCallbacks.push(() => purchaseObserver.disconnect());

    const footer = document.querySelector('.site-footer');
    if (footer) {
      const footerObserver = new IntersectionObserver(([entry]) => {
        footerIsVisible = entry.isIntersecting;
        updateStickyCart();
      });
      footerObserver.observe(footer);
      cleanupCallbacks.push(() => footerObserver.disconnect());
    }
  }

  if (stickySubmit) {
    stickySubmit.addEventListener('click', onSubmitNeedsSelection);
    cleanupCallbacks.push(() => stickySubmit.removeEventListener('click', onSubmitNeedsSelection));
  }

  updateVariant({ updateUrl: false });
  productSectionCleanups.set(section, () => {
    cleanupCallbacks.forEach((cleanup) => cleanup());
    productSectionCleanups.delete(section);
  });
};

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.observer) return;
    this.observer = new IntersectionObserver((entries, currentObserver) => {
      if (!entries[0].isIntersecting) return;

      currentObserver.unobserve(this);
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const recommendations = html.querySelector('product-recommendations');
          if (recommendations?.innerHTML.trim()) this.innerHTML = recommendations.innerHTML;
        })
        .catch(() => {});
    }, { rootMargin: '0px 0px 300px 0px' });

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

const announce = (message) => {
  const status = document.querySelector('[data-global-status]');
  if (!status || !message) return;
  status.textContent = '';
  window.setTimeout(() => { status.textContent = message; }, 60);
};

const buildCatalogUrl = (form) => {
  const url = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
  const params = new URLSearchParams(new FormData(form));
  Array.from(params.keys()).forEach((key) => {
    if (params.getAll(key).every((value) => value === '')) params.delete(key);
  });
  url.search = params.toString();
  return url;
};

let catalogController = null;

const renderCatalog = async (url, { push = true } = {}) => {
  const section = document.querySelector('[data-collection-section]');
  if (!section || !window.fetch || !window.DOMParser) {
    window.location.href = url.toString();
    return;
  }

  catalogController?.abort();
  const controller = new AbortController();
  catalogController = controller;
  const catalog = section.querySelector('[data-catalog]');
  const facetsWereOpen = section.querySelector('[data-facets]')?.classList.contains('is-open');
  const facetStates = Array.from(section.querySelectorAll('details.facet')).map((details) => [details.id, details.open]);
  const focusedId = document.activeElement?.id;
  const groupsScroll = section.querySelector('.facets-form__groups')?.scrollTop || 0;
  catalog?.setAttribute('aria-busy', 'true');

  try {
    const fetchUrl = new URL(url.toString());
    fetchUrl.searchParams.set('section_id', section.dataset.sectionId);
    const response = await fetch(fetchUrl.toString(), { signal: controller.signal });
    if (!response.ok) throw new Error(response.statusText);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const nextSection = html.querySelector('[data-collection-section]');
    if (!nextSection) throw new Error('Missing collection section');

    facetStates.forEach(([id, open]) => {
      const details = id ? nextSection.querySelector(`#${CSS.escape(id)}`) : null;
      if (details) details.open = open;
    });
    section.replaceWith(nextSection);
    initializeTheme(nextSection);
    if (facetsWereOpen) nextSection.querySelector('[data-facets-open]')?.click();
    const groups = nextSection.querySelector('.facets-form__groups');
    if (groups) groups.scrollTop = groupsScroll;
    if (focusedId) {
      window.setTimeout(() => document.getElementById(focusedId)?.focus({ preventScroll: true }), 40);
    }
    if (push) window.history.pushState({ catalog: true }, '', url.toString());
    announce(nextSection.querySelector('[data-results-count]')?.textContent.trim());
  } catch (error) {
    if (error.name === 'AbortError') return;
    window.location.href = url.toString();
  } finally {
    if (catalogController === controller) catalogController = null;
  }
};

let facetTimer = null;

document.addEventListener('change', (event) => {
  const facetsForm = event.target.closest('[data-facets-form]');
  if (facetsForm) {
    window.clearTimeout(facetTimer);
    const delay = event.target.type === 'number' ? 0 : 250;
    facetTimer = window.setTimeout(() => renderCatalog(buildCatalogUrl(facetsForm)), delay);
    return;
  }

  const sortSelect = event.target.closest('[data-sort-select]');
  if (sortSelect?.form) {
    if (sortSelect.closest('[data-collection-section]')) {
      renderCatalog(buildCatalogUrl(sortSelect.form));
    } else {
      sortSelect.form.submit();
    }
  }
});

document.addEventListener('submit', (event) => {
  const facetsForm = event.target.closest('[data-facets-form]');
  if (!facetsForm || !window.fetch) return;
  event.preventDefault();
  const facets = facetsForm.closest('[data-facets]');
  if (facets?.classList.contains('is-open')) {
    facets.querySelector('[data-facets-close]')?.click();
    return;
  }
  renderCatalog(buildCatalogUrl(facetsForm));
});

document.addEventListener('click', (event) => {
  const facetLink = event.target.closest('[data-facet-link]');
  if (!facetLink || !document.querySelector('[data-collection-section]')) return;
  event.preventDefault();
  renderCatalog(new URL(facetLink.href, window.location.origin));
});

window.addEventListener('popstate', () => {
  if (document.querySelector('[data-collection-section]')) {
    renderCatalog(new URL(window.location.href), { push: false });
  }
});

const initializeFacets = (root) => {
  queryWithin(root, '[data-facets-open]').forEach((button) => {
    if (button.dataset.facetsInitialized === 'true') return;
    const facets = document.getElementById(button.getAttribute('aria-controls'));
    if (!facets) return;
    button.dataset.facetsInitialized = 'true';

    const closeButton = facets.querySelector('[data-facets-close]');
    const overlay = facets.parentElement?.querySelector('[data-facets-overlay]');
    const close = ({ restoreFocus = true } = {}) => {
      facets.classList.remove('is-open');
      facets.removeAttribute('role');
      facets.removeAttribute('aria-modal');
      overlay?.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
      updateBodyScrollLock();
      if (restoreFocus) button.focus();
    };

    button.addEventListener('click', (event) => {
      event.preventDefault();
      facets.classList.add('is-open');
      facets.setAttribute('role', 'dialog');
      facets.setAttribute('aria-modal', 'true');
      overlay?.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
      updateBodyScrollLock();
      window.setTimeout(() => closeButton?.focus(), 0);
    });
    closeButton?.addEventListener('click', () => close());
    overlay?.addEventListener('click', () => close());
    facets.addEventListener('keydown', (event) => {
      if (!facets.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusableElements = getFocusableElements(facets);
      if (!focusableElements.length) return;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    });
  });
};

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Generic modal dialogs ([data-modal] + [data-modal-open="Id"]) */

let modalReturnFocus = null;

function openModal(modal, trigger) {
  if (!modal) return;
  modalReturnFocus = trigger || document.activeElement;
  modal.hidden = false;
  updateBodyScrollLock();
  requestAnimationFrame(() => {
    const target = modal.querySelector('[data-modal-close]:not([tabindex="-1"])') || modal.querySelector('[role="dialog"]');
    target?.focus();
  });
}

function closeModal(modal, { restoreFocus = true } = {}) {
  if (!modal || modal.hidden) return;
  modal.hidden = true;
  updateBodyScrollLock();
  if (restoreFocus && modalReturnFocus instanceof HTMLElement && modalReturnFocus.isConnected) modalReturnFocus.focus();
}

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-modal-open]');
  if (opener) {
    const modal = document.getElementById(opener.dataset.modalOpen);
    if (!modal) return;
    event.preventDefault();
    openModal(modal, opener);
    return;
  }
  const closer = event.target.closest('[data-modal-close]');
  if (closer) closeModal(closer.closest('[data-modal]'));
});

document.addEventListener('keydown', (event) => {
  const modal = document.querySelector('[data-modal]:not([hidden])');
  if (!modal || getCartDrawer()?.classList.contains('is-open')) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    closeModal(modal);
    return;
  }
  if (event.key !== 'Tab') return;
  const focusableElements = getFocusableElements(modal);
  if (!focusableElements.length) return;
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  }
});

/* Quick view for multi-variant product cards */

document.addEventListener('click', async (event) => {
  const trigger = event.target.closest('[data-quick-view]');
  const modal = document.getElementById('QuickView');
  if (!trigger || !modal || !window.fetch || !window.DOMParser) return;
  event.preventDefault();

  const content = modal.querySelector('[data-quick-view-content]');
  content.innerHTML = '<div class="modal__loading"><span class="spinner"></span></div>';
  openModal(modal, trigger);

  try {
    const url = new URL(trigger.dataset.quickView, window.location.origin);
    url.searchParams.set('view', 'quick-view');
    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(response.statusText);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const section = html.querySelector('[data-product-section]');
    if (!section) throw new Error('Missing product section');

    html.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
      const href = link.getAttribute('href');
      if (href && !document.querySelector(`link[href="${CSS.escape(href)}"]`)) document.head.appendChild(link.cloneNode());
    });
    content.replaceChildren(document.importNode(section, true));
    initializeTheme(content);
    window.Shopify?.PaymentButton?.init?.();
  } catch (error) {
    closeModal(modal, { restoreFocus: false });
    window.location.href = trigger.getAttribute('href') || trigger.dataset.quickView;
  }
});

/* Header: sticky offset, scroll state, mega menu hover intent, mobile search */

const openHeaderSearch = (header) => {
  header.classList.add('is-search-open');
  header.querySelector('[data-search-open]')?.setAttribute('aria-expanded', 'true');
  updateBodyScrollLock();
  requestAnimationFrame(() => header.querySelector('[data-search-input]')?.focus());
};

const closeHeaderSearch = (header, { restoreFocus = true } = {}) => {
  if (!header?.classList.contains('is-search-open')) return;
  header.classList.remove('is-search-open');
  const toggle = header.querySelector('[data-search-open]');
  toggle?.setAttribute('aria-expanded', 'false');
  updateBodyScrollLock();
  if (restoreFocus) toggle?.focus();
};

const initializeHeader = (root) => {
  queryWithin(root, '[data-site-header]').forEach((header) => {
    if (header.dataset.headerInitialized === 'true') return;
    header.dataset.headerInitialized = 'true';

    const wrapper = header.closest('.shopify-section') || header.parentElement;
    const sticky = header.classList.contains('site-header--sticky');
    wrapper?.classList.toggle('is-sticky', sticky);

    const setOffset = () => {
      const height = sticky ? Math.round(header.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--header-offset', `${height}px`);
    };
    setOffset();
    if ('ResizeObserver' in window) new ResizeObserver(setOffset).observe(header);

    const onScroll = () => wrapper?.classList.toggle('is-scrolled', window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    header.querySelectorAll('[data-mega-menu]').forEach((details) => {
      let closeTimer = null;
      const summary = details.querySelector(':scope > summary');
      details.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        window.clearTimeout(closeTimer);
        header.querySelectorAll('[data-mega-menu][open]').forEach((other) => {
          if (other !== details) other.open = false;
        });
        if (!details.open) {
          details.open = true;
          details.dataset.hoverOpened = 'true';
        }
      });
      details.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        closeTimer = window.setTimeout(() => {
          details.open = false;
          delete details.dataset.hoverOpened;
        }, 180);
      });
      summary?.addEventListener('click', (event) => {
        if (details.dataset.hoverOpened === 'true' && details.open) {
          event.preventDefault();
          delete details.dataset.hoverOpened;
        }
      });
    });

    header.querySelector('[data-search-open]')?.addEventListener('click', () => openHeaderSearch(header));
  });
};

/* Predictive search */

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.input = this.querySelector('[data-search-input]');
    this.panel = this.querySelector('[data-search-panel]');
    this.intro = this.querySelector('[data-search-intro]');
    this.results = this.querySelector('[data-search-results]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.status = this.querySelector('[data-search-status]');
    this.header = this.closest('[data-site-header]');
    this.cache = new Map();
    this.enabled = this.dataset.enabled !== 'false' && Boolean(window.fetch && window.DOMParser);
    if (!this.input || !this.panel) return;

    this.input.addEventListener('focus', () => this.open());
    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (event) => this.onInputKeydown(event));
    this.panel.addEventListener('keydown', (event) => this.onPanelKeydown(event));
    this.clearButton?.addEventListener('click', () => {
      this.input.value = '';
      this.onInput();
      this.input.focus();
    });
    this.querySelector('[data-search-close]')?.addEventListener('click', () => this.close({ closeOverlay: true }));
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target) && !event.target.closest('[data-search-open]')) this.close();
    };
    document.addEventListener('click', this.onDocumentClick);
    if (this.input.value.trim()) this.onInput({ openPanel: false });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    this.controller?.abort();
  }

  open() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close({ closeOverlay = false } = {}) {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    if (closeOverlay && this.header) closeHeaderSearch(this.header);
  }

  onInput({ openPanel = true } = {}) {
    const term = this.input.value.trim();
    if (this.clearButton) this.clearButton.hidden = term.length === 0;
    window.clearTimeout(this.timer);
    if (term.length < 2 || !this.enabled) {
      this.results.innerHTML = '';
      if (this.intro) this.intro.hidden = false;
      if (openPanel) this.open();
      return;
    }
    this.timer = window.setTimeout(() => this.search(term, { openPanel }), 220);
  }

  async search(term, { openPanel = true } = {}) {
    if (openPanel) this.open();
    if (this.cache.has(term)) {
      this.render(this.cache.get(term));
      return;
    }
    this.controller?.abort();
    this.controller = new AbortController();
    if (this.intro) this.intro.hidden = true;
    this.results.innerHTML = '<div class="predictive-search__loading"><span class="spinner"></span></div>';
    if (this.status) this.status.textContent = this.dataset.loadingLabel || '';

    try {
      const params = new URLSearchParams({
        q: term,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,query,page,article',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each'
      });
      const response = await fetch(`${this.dataset.url}?${params.toString()}`, { signal: this.controller.signal });
      if (!response.ok) throw new Error(response.statusText);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const markup = html.querySelector('[data-predictive-search-content]')?.outerHTML || '';
      this.cache.set(term, markup);
      if (this.input.value.trim() === term) this.render(markup);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.results.innerHTML = '';
      if (this.intro) this.intro.hidden = false;
    }
  }

  render(markup) {
    if (this.intro) this.intro.hidden = Boolean(markup);
    this.results.innerHTML = markup;
    const count = this.results.querySelector('[data-predictive-search-content]')?.dataset.resultCount || '0';
    if (this.status) this.status.textContent = (this.dataset.resultsLabel || '').replace('__count__', count);
  }

  getLinks() {
    return Array.from(this.panel.querySelectorAll('a[href]')).filter((link) => link.getClientRects().length > 0);
  }

  onInputKeydown(event) {
    if (event.key === 'ArrowDown') {
      const links = this.getLinks();
      if (!links.length) return;
      event.preventDefault();
      this.open();
      links[0].focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (!this.panel.hidden) {
        this.close();
      } else {
        this.close({ closeOverlay: true });
      }
    }
  }

  onPanelKeydown(event) {
    const links = this.getLinks();
    const index = links.indexOf(document.activeElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      links[Math.min(index + 1, links.length - 1)]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (index <= 0) this.input.focus();
      else links[index - 1].focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      this.input.focus();
    }
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}

/* Announcement bar rotation */

const initializeAnnouncements = (root) => {
  queryWithin(root, '[data-announcement]').forEach((bar) => {
    const items = Array.from(bar.querySelectorAll('[data-announcement-item]'));
    if (items.length < 2 || bar.dataset.announcementInitialized === 'true') return;
    bar.dataset.announcementInitialized = 'true';

    let index = 0;
    let timer = null;
    let paused = false;
    const show = (nextIndex) => {
      index = (nextIndex + items.length) % items.length;
      items.forEach((item, itemIndex) => {
        item.hidden = itemIndex !== index;
      });
    };
    const schedule = () => {
      window.clearInterval(timer);
      if (bar.dataset.autoplay !== 'true' || prefersReducedMotion()) return;
      timer = window.setInterval(() => {
        if (!bar.isConnected) {
          window.clearInterval(timer);
          return;
        }
        if (!paused && document.visibilityState === 'visible') show(index + 1);
      }, Number(bar.dataset.interval) || 5000);
    };

    bar.querySelector('[data-announcement-prev]')?.addEventListener('click', () => {
      show(index - 1);
      schedule();
    });
    bar.querySelector('[data-announcement-next]')?.addEventListener('click', () => {
      show(index + 1);
      schedule();
    });
    bar.addEventListener('mouseenter', () => { paused = true; });
    bar.addEventListener('mouseleave', () => { paused = false; });
    bar.addEventListener('focusin', () => { paused = true; });
    bar.addEventListener('focusout', () => { paused = false; });
    bar.addEventListener('announcement:show', (event) => {
      const itemIndex = items.indexOf(event.detail);
      if (itemIndex >= 0) show(itemIndex);
      paused = true;
    });
    bar.addEventListener('announcement:resume', () => { paused = false; });
    schedule();
  });
};

/* Sliders and tabs */

const getActiveSlider = (container) => Array.from(container.querySelectorAll('[data-slider]'))
  .find((slider) => !slider.closest('[hidden]'));

const updateSliderButtons = (container) => {
  const buttons = container.querySelector('[data-slider-buttons]');
  if (!buttons) return;
  const slider = getActiveSlider(container);
  if (!slider) {
    buttons.hidden = true;
    return;
  }
  const maxScroll = slider.scrollWidth - slider.clientWidth;
  buttons.hidden = maxScroll <= 4;
  const previous = buttons.querySelector('[data-slider-prev]');
  const next = buttons.querySelector('[data-slider-next]');
  if (previous) previous.disabled = slider.scrollLeft <= 4;
  if (next) next.disabled = slider.scrollLeft >= maxScroll - 4;
};

const initializeSliders = (root) => {
  queryWithin(root, '[data-slider-buttons]').forEach((buttons) => {
    if (buttons.dataset.sliderInitialized === 'true') return;
    buttons.dataset.sliderInitialized = 'true';
    const container = buttons.closest('.shopify-section') || buttons.closest('section') || document.body;

    const step = (direction) => {
      const slider = getActiveSlider(container);
      if (!slider) return;
      const gap = Number.parseFloat(getComputedStyle(slider).columnGap) || 0;
      const itemWidth = (slider.firstElementChild?.getBoundingClientRect().width || slider.clientWidth) + gap;
      const perPage = Math.max(1, Math.floor((slider.clientWidth + gap) / itemWidth));
      slider.scrollBy({ left: direction * itemWidth * perPage, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    };

    buttons.querySelector('[data-slider-prev]')?.addEventListener('click', () => step(-1));
    buttons.querySelector('[data-slider-next]')?.addEventListener('click', () => step(1));
    container.querySelectorAll('[data-slider]').forEach((slider) => {
      slider.addEventListener('scroll', () => updateSliderButtons(container), { passive: true });
    });
    window.addEventListener('resize', () => updateSliderButtons(container));
    updateSliderButtons(container);
  });
};

const initializeTabs = (root) => {
  queryWithin(root, '[data-tabs]').forEach((tablist) => {
    if (tablist.dataset.tabsInitialized === 'true') return;
    tablist.dataset.tabsInitialized = 'true';
    const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));

    const activate = (tab, { focus = false } = {}) => {
      tabs.forEach((candidate) => {
        const selected = candidate === tab;
        candidate.setAttribute('aria-selected', String(selected));
        candidate.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(candidate.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
      if (focus) tab.focus();
      const container = tablist.closest('.shopify-section') || tablist.parentElement;
      if (container) updateSliderButtons(container);
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        const targets = {
          ArrowRight: tabs[(index + 1) % tabs.length],
          ArrowLeft: tabs[(index - 1 + tabs.length) % tabs.length],
          Home: tabs[0],
          End: tabs[tabs.length - 1]
        };
        const target = targets[event.key];
        if (!target) return;
        event.preventDefault();
        activate(target, { focus: true });
      });
    });
  });
};

/* Cart page quantity steppers (standard cart form stays the fallback) */

const submitCartPage = (form) => {
  window.clearTimeout(Number(form.dataset.submitTimer));
  form.dataset.submitTimer = String(window.setTimeout(() => {
    const updateButton = form.querySelector('[data-cart-update]');
    if (form.requestSubmit && updateButton) form.requestSubmit(updateButton);
    else form.submit();
  }, 350));
};

document.addEventListener('click', (event) => {
  const stepButton = event.target.closest('[data-cart-page-quantity] [data-step]');
  if (!stepButton) return;
  const input = stepButton.closest('[data-cart-page-quantity]').querySelector('input');
  if (!input?.form) return;
  const step = Number(input.step) || 1;
  input.value = String(Math.max(0, (Number(input.value) || 0) + (Number(stepButton.dataset.step) * step)));
  submitCartPage(input.form);
});

document.addEventListener('change', (event) => {
  const cartInput = event.target.closest('[data-cart-quantity]');
  if (cartInput?.form) {
    submitCartPage(cartInput.form);
    return;
  }
  const localizationSelect = event.target.closest('[data-localization-select]');
  if (localizationSelect?.form) localizationSelect.form.submit();
});

/* Share: copy link */

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-copy-link]');
  if (!button) return;
  try {
    await navigator.clipboard.writeText(button.dataset.copyLink);
    announce(button.dataset.copiedLabel);
    button.classList.add('is-copied');
    window.setTimeout(() => button.classList.remove('is-copied'), 1600);
  } catch (error) {
    window.prompt('', button.dataset.copyLink);
  }
});

const mobileOverlayViewport = window.matchMedia('(max-width: 989px)');
mobileOverlayViewport.addEventListener('change', (event) => {
  if (event.matches) return;

  document.querySelectorAll('[data-mobile-navigation][open]').forEach((navigation) => {
    navigation.open = false;
  });
  document.querySelectorAll('.site-header.is-search-open').forEach((header) => closeHeaderSearch(header, { restoreFocus: false }));
  updateBodyScrollLock();
});

const initializeTheme = (root = document) => {
  initializeHeader(root);
  initializeMobileNavigation(root);
  initializeAnnouncements(root);
  queryWithin(root, '[data-product-section]').forEach(initializeProductSection);
  initializeFacets(root);
  initializeTabs(root);
  initializeSliders(root);
  updateCartCount(getCartDrawer());
  syncWishlistButtons(root);
  updateWishlistCount();
};

initializeTheme();

document.addEventListener('shopify:section:load', (event) => initializeTheme(event.target));

// Theme editor: reveal the drawer, slide or tab that the merchant selects in the sidebar.
document.addEventListener('shopify:section:select', (event) => {
  if (event.target.querySelector('[data-cart-drawer]')) openCartDrawer();
});

document.addEventListener('shopify:section:deselect', (event) => {
  if (event.target.querySelector('[data-cart-drawer]')) closeCartDrawer({ restoreFocus: false });
});

document.addEventListener('shopify:block:select', (event) => {
  const block = event.target;
  if (block.matches('[data-announcement-item]')) {
    block.closest('[data-announcement]')?.dispatchEvent(new CustomEvent('announcement:show', { detail: block }));
  }
  if (block.matches('[role="tab"]')) block.click();
  const slide = block.closest('[data-slider] > *');
  if (slide) slide.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
});

document.addEventListener('shopify:block:deselect', (event) => {
  event.target.closest('[data-announcement]')?.dispatchEvent(new CustomEvent('announcement:resume'));
});

document.addEventListener('click', async (event) => {
  const shareButton = event.target.closest('[data-share]');
  if (!shareButton) return;
  const url = shareButton.dataset.shareUrl || window.location.href;
  if (navigator.share) {
    try {
      await navigator.share({ title: shareButton.dataset.shareTitle || document.title, url });
    } catch (error) {
      // The customer closed the share sheet.
    }
    return;
  }
  try {
    await navigator.clipboard.writeText(url);
    announce(shareButton.dataset.copiedLabel);
    shareButton.classList.add('is-copied');
    window.setTimeout(() => shareButton.classList.remove('is-copied'), 2000);
  } catch (error) {
    window.prompt(shareButton.dataset.copyPrompt || '', url);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  queryWithin(event.target, '[data-product-section]').forEach((section) => {
    productSectionCleanups.get(section)?.();
  });
});